import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CountryMark, ServiceMark, SmsIcon } from '../sms/SmsMarks';
import {
  disableLongRentalRenewal,
  createLongRentalCallAccess,
  createClientOperationKey,
  disableLongRentalShare,
  fetchLongRentalNumbers,
  fetchSharedLongRentalNumbers,
  getApiErrorMessage,
  previewLongRentalRenewal,
  refundLongRental,
  renewLongRental,
  removeSharedLongRentalNumber,
  rotateLongRentalShare,
  syncLongRentalNumber,
  wakeLongRentalNumber,
} from '../../services/smsApi';
import { useBrowserVoiceLine } from '../voice/useBrowserVoiceLine';
import { LongRentalNumber, LongRentalRenewalPreview, SharedLongRentalNumber } from '../../types/models';
import { formatDateTime, formatUsd } from '../../utils/format';
import { presentPhoneNumber } from '../../utils/phone';
import PublicInboxLink from './PublicInboxLink';

function stateLabel(state: LongRentalNumber['state']): string {
  return {
    active: 'Активен',
    overdue: 'Ожидает продления',
    expired: 'Срок истёк',
    refunded: 'Возвращён',
    unknown: 'Статус уточняется',
  }[state];
}

const LongRentalNumbersSection: React.FC = () => {
  const [items, setItems] = useState<LongRentalNumber[]>([]);
  const [sharedItems, setSharedItems] = useState<SharedLongRentalNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [previewById, setPreviewById] = useState<Record<string, LongRentalRenewalPreview | undefined>>({});
  const [acceptedById, setAcceptedById] = useState<Record<string, boolean | undefined>>({});
  const [renewOperationKeyById, setRenewOperationKeyById] = useState<Record<string, string | undefined>>({});
  const [refundConfirmId, setRefundConfirmId] = useState<string | null>(null);
  const [capabilityFilter, setCapabilityFilter] = useState<'all' | 'sms' | 'sms_voice'>('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const mounted = useRef(true);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const next = await fetchLongRentalNumbers();
      if (mounted.current) {
        setItems(next);
        setError('');
        if (!quiet) setLoading(false);
      }
      try {
        const shared = await fetchSharedLongRentalNumbers();
        if (mounted.current) setSharedItems(shared);
      } catch { /* собственные номера остаются доступны */ }
    } catch (loadError) {
      if (mounted.current && !quiet) setError(getApiErrorMessage(loadError));
    } finally {
      if (mounted.current && !quiet) setLoading(false);
    }
  }, []);

  const { lineId, lineState, prepareLine, acceptCall, finishCall, sendDigit } = useBrowserVoiceLine(
    createLongRentalCallAccess,
    setError,
    () => { void load(true); },
  );

  useEffect(() => {
    mounted.current = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    const tick = async () => {
      if (document.visibilityState === 'visible') await load(true);
      if (!stopped) timer = setTimeout(tick, 10_000);
    };
    void load().finally(() => {
      if (!stopped) timer = setTimeout(tick, 10_000);
    });
    return () => {
      stopped = true;
      mounted.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [load]);

  const run = async (id: string, action: () => Promise<unknown>) => {
    setPendingId(id);
    setError('');
    try {
      await action();
      await load(true);
    } catch (actionError) {
      setError(getApiErrorMessage(actionError));
    } finally {
      setPendingId(null);
    }
  };

  const openRenewal = async (item: LongRentalNumber) => {
    setPendingId(item.id);
    setError('');
    try {
      const preview = await previewLongRentalRenewal(item.id);
      setPreviewById((current) => ({ ...current, [item.id]: preview }));
      setAcceptedById((current) => ({ ...current, [item.id]: false }));
      setRenewOperationKeyById((current) => ({ ...current, [item.id]: current[item.id] || createClientOperationKey('long-renew') }));
    } catch (previewError) {
      setError(getApiErrorMessage(previewError));
    } finally {
      setPendingId(null);
    }
  };

  const services = useMemo(() => {
    const byCode = new Map<string, { code: string; name: string }>();
    for (const item of items) {
      if (capabilityFilter !== 'all' && item.rentalCapability !== capabilityFilter) continue;
      const code = item.service?.code || 'all_services';
      if (!byCode.has(code)) byCode.set(code, { code, name: item.service?.name || 'Любые сервисы' });
    }
    return Array.from(byCode.values()).sort((left, right) => left.name.localeCompare(right.name, 'ru'));
  }, [capabilityFilter, items]);

  const effectiveServiceFilter = serviceFilter === 'all' || services.some((service) => service.code === serviceFilter)
    ? serviceFilter : 'all';

  const counts = useMemo(() => ({
    all: items.length,
    sms: items.filter((item) => item.rentalCapability === 'sms').length,
    sms_voice: items.filter((item) => item.rentalCapability === 'sms_voice').length,
  }), [items]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase('ru-RU');
    return items.filter((item) => {
      if (capabilityFilter !== 'all' && item.rentalCapability !== capabilityFilter) return false;
      if (effectiveServiceFilter !== 'all' && (item.service?.code || 'all_services') !== effectiveServiceFilter) return false;
      if (!query) return true;
      const searchable = [
        item.phoneNumber,
        item.service?.name,
        item.location.stateName,
        item.location.areaCode,
        ...item.messages.flatMap((message) => [message.sender, message.text]),
      ].filter(Boolean).join(' ').toLocaleLowerCase('ru-RU');
      return searchable.includes(query);
    });
  }, [capabilityFilter, effectiveServiceFilter, items, searchQuery]);

  return (
    <section className="sms-longNumbers" aria-labelledby="long-rental-numbers-title">
      <div className="sms-longNumbers__head">
        <div>
          <h2 id="long-rental-numbers-title">Долгосрочная аренда</h2>
          <p>Входящие SMS сохраняются потоком; у номеров SMS + Voice здесь же открывается линия звонка.</p>
        </div>
        <Link to="/long-term-rent" className="u2-btn is-secondary is-sm">Арендовать номер</Link>
      </div>

      {error ? <div className="sms-inlineAlert is-error" role="alert">{error}</div> : null}
      {loading ? <div className="sms-longRent__loading" role="status">Загружаем арендованные номера…</div> : null}
      {!loading && items.length === 0 && sharedItems.length === 0 ? (
        <div className="sms-longNumbers__empty">
          <SmsIcon name="phone" />
          <span><strong>Долгосрочных номеров пока нет</strong><small>После оплаты номер и поток СМС появятся в этом разделе.</small></span>
        </div>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="sms-longNumbers__filters" aria-label="Фильтры долгосрочной аренды">
          <div className="sms-longNumbers__typeFilter" role="radiogroup" aria-label="Тип долгосрочного номера">
            {([
              ['all', 'Все'],
              ['sms', 'SMS'],
              ['sms_voice', 'SMS + Voice'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={capabilityFilter === value}
                className={capabilityFilter === value ? 'is-selected' : ''}
                onClick={() => { setCapabilityFilter(value); setServiceFilter('all'); }}
              >
                <span>{label}</span><small>{counts[value]}</small>
              </button>
            ))}
          </div>
          <label className="sms-searchField sms-longNumbers__search">
            <span className="sr-only">Поиск по долгосрочным номерам</span>
            <SmsIcon name="search" />
            <input className="u2-input" type="search" value={searchQuery} placeholder="Номер, сервис или сообщение" onChange={(event) => setSearchQuery(event.target.value)} />
          </label>
          <label className="sms-field sms-longNumbers__serviceFilter">
            <span className="sr-only">Сервис долгосрочной аренды</span>
            <select className="u2-input" value={effectiveServiceFilter} onChange={(event) => setServiceFilter(event.target.value)}>
              <option value="all">Все сервисы</option>
              {services.map((service) => <option key={service.code} value={service.code}>{service.name}</option>)}
            </select>
          </label>
        </div>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="sms-longNumbers__resultMeta" aria-live="polite">
          <span>Найдено: <strong>{filteredItems.length}</strong></span>
          {effectiveServiceFilter !== 'all' ? <span>Сервис: <strong>{services.find((service) => service.code === effectiveServiceFilter)?.name}</strong></span> : null}
        </div>
      ) : null}

      <div className="sms-longNumbers__list">
        {filteredItems.map((item) => {
          const phone = presentPhoneNumber(item.phoneNumber);
          const preview = previewById[item.id];
          const busy = pendingId === item.id;
          const activeLine = lineId === item.id;
          return (
            <article className="sms-longNumber" key={item.id}>
              <div className="sms-longNumber__top">
                <div className="sms-longNumber__identity">
                  <div className="sms-longNumber__marks">
                    <ServiceMark code={item.service?.code || 'all'} name={item.service?.name || 'Любые сервисы'} />
                    <CountryMark name="США" />
                  </div>
                  <span>
                    <strong>{phone.displayFullNumber}</strong>
                    <small>{item.rentalCapability === 'sms_voice' ? 'SMS + Voice · ' : ''}{item.service?.name || 'Любые сервисы'} · {item.durationDays} дней · до {item.endsAt ? formatDateTime(item.endsAt) : 'уточняется'}</small>
                  </span>
                </div>
                <span className={`sms-longNumber__state is-${item.state}`}>{stateLabel(item.state)}</span>
              </div>

              <div className="sms-longNumber__body">
                <section className="sms-longNumber__stream" aria-label={`Поток СМС ${phone.displayFullNumber}`}>
                  {item.rentalCapability === 'sms_voice' ? (
                    <div className="sms-longNumber__voiceLine">
                      <div className="sms-longNumber__streamHead"><strong>Входящий звонок</strong><span>{item.calls.length}</span></div>
                      {!activeLine || lineState === 'idle' || lineState === 'ended' ? <button type="button" className="u2-btn is-primary is-sm" disabled={item.state !== 'active' || (item.wake.available && item.wake.status !== 'ready')} onClick={() => void prepareLine(item.id)}>Подготовить Voice-линию</button> : null}
                      {activeLine && lineState === 'registering' ? <span className="sms-voiceNumber__lineState">Подключаем линию…</span> : null}
                      {activeLine && lineState === 'ready' ? <span className="sms-voiceNumber__lineState is-ready">Линия готова. Запросите звонок в выбранном сервисе.</span> : null}
                      {activeLine && lineState === 'ringing' ? <div className="sms-voiceNumber__incoming"><strong>Входящий звонок</strong><button type="button" className="u2-btn is-primary is-sm" onClick={acceptCall}>Ответить</button><button type="button" className="u2-btn is-secondary is-sm" onClick={finishCall}>Отклонить</button></div> : null}
                      {activeLine && lineState === 'connected' ? <div className="sms-voiceNumber__connected"><strong>Звонок подключён</strong><div className="sms-voiceNumber__keypad" aria-label="Тоновый набор">{['1','2','3','4','5','6','7','8','9','*','0','#'].map((digit) => <button type="button" key={digit} onClick={() => sendDigit(digit)}>{digit}</button>)}</div><button type="button" className="u2-btn is-secondary is-sm" onClick={finishCall}>Завершить</button></div> : null}
                    </div>
                  ) : null}
                  {item.wake.available ? (
                    <div className="sms-longNumber__voiceLine" role="status">
                      <div className="sms-longNumber__streamHead"><strong>Ручная активация номера</strong></div>
                      {item.wake.status === 'ready' ? <p>Номер готов принимать сообщения{item.wake.readyUntil ? ` до ${formatDateTime(item.wake.readyUntil)}` : ''}.</p> : null}
                      {item.wake.status === 'scheduled' ? <p>Активация запланирована{item.wake.scheduledFor ? ` на ${formatDateTime(item.wake.scheduledFor)}` : ''}. Дождитесь готовности перед запросом кода.</p> : null}
                      {item.wake.status === 'sleeping' ? <p>Перед запросом SMS или звонка разбудите номер. Подготовка может занять несколько минут; окно приёма обычно около 5 минут.</p> : null}
                      {item.wake.status !== 'ready' ? <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => void run(item.id, () => wakeLongRentalNumber(item.id))}>{item.wake.status === 'scheduled' ? 'Обновить готовность' : 'Разбудить номер'}</button> : null}
                    </div>
                  ) : null}
                  {item.wake.status === 'unknown' && item.state === 'active' ? <p className="sms-longNumber__waiting">Проверяем режим приёма номера…</p> : null}
                  <div className="sms-longNumber__streamHead"><strong>Поток СМС</strong><span>{item.messages.length}</span></div>
                  {item.messages.length ? item.messages.map((message) => (
                    <article className="sms-longNumber__message" key={message.id}>
                      <div><strong>{message.sender || 'Отправитель не указан'}</strong><time dateTime={message.receivedAt}>{formatDateTime(message.receivedAt)}</time></div>
                      <p>{message.text}</p>
                    </article>
                  )) : (
                    <div className="sms-longNumber__waiting"><span aria-hidden="true" /><p><strong>Ожидаем первое сообщение</strong><small>Проверяем входящие автоматически</small></p></div>
                  )}
                </section>

                <aside className="sms-longNumber__aside">
                  <PublicInboxLink path={item.publicInboxPath} compact />
                  {item.publicInboxPath ? <div className="sms-longNumber__shareActions">
                    <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => {
                      if (window.confirm('Перевыпустить ссылку? Старая ссылка и сохранённые закладки перестанут работать.')) {
                        void run(item.id, () => rotateLongRentalShare(item.id));
                      }
                    }}>Перевыпустить ссылку</button>
                    <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => {
                      if (window.confirm('Отключить доступ по ссылке? Все внешние пользователи и их закладки потеряют доступ к номеру.')) {
                        void run(item.id, () => disableLongRentalShare(item.id));
                      }
                    }}>Отключить доступ</button>
                  </div> : <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => void run(item.id, () => rotateLongRentalShare(item.id))}>Создать ссылку для доступа</button>}
                  <dl>
                    <div><dt>Назначение</dt><dd>{item.service?.name || 'Любые сервисы'}</dd></div>
                    <div><dt>Регион</dt><dd>{item.location.areaCode ? `${item.location.stateName || 'США'} · ${item.location.areaCode}` : (item.location.stateName || 'Любой штат')}</dd></div>
                    <div><dt>Оплачено</dt><dd>{formatUsd(item.finalAmount)}</dd></div>
                    {item.rentalCapability === 'sms_voice' ? <div><dt>Звонков</dt><dd>{item.calls.length}</dd></div> : null}
                    <div><dt>Продление</dt><dd>{item.renewal.canRenew ? 'Доступно вручную' : 'Недоступно'}</dd></div>
                    {item.renewal.renewable ? <div><dt>Автопродление</dt><dd>{item.renewal.enabled ? 'Включено' : 'Выключено'}</dd></div> : null}
                  </dl>
                  <details>
                    <summary>Условия номера</summary>
                    <p><strong>Отмена:</strong> {item.cancellation.rule}</p>
                    <p><strong>Возврат:</strong> {item.refund.rule}</p>
                    <p><strong>Продление:</strong> {item.renewal.rule}</p>
                  </details>
                  <div className="sms-longNumber__actions">
                    <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => void run(item.id, () => syncLongRentalNumber(item.id))}>Обновить данные</button>
                    {item.renewal.canRenew ? <button type="button" className="u2-btn is-primary is-sm" disabled={busy} onClick={() => void openRenewal(item)}>Продлить</button> : null}
                    {item.cancellation.canDisableRenewal ? <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => void run(item.id, () => disableLongRentalRenewal(item.id))}>Отключить продление</button> : null}
                    {item.refund.available ? <button type="button" className="u2-btn is-danger is-sm" disabled={busy} onClick={() => setRefundConfirmId(item.id)}>Вернуть оплату</button> : null}
                  </div>
                </aside>
              </div>

              {preview ? (
                <div className="sms-longNumber__renewal">
                  <div><strong>Продление на {preview.durationDays} дней</strong><span>{formatUsd(preview.finalAmount)}</span></div>
                  <p>{preview.terms.renewal}</p>
                  <p>{preview.terms.refund}</p>
                  <label><input type="checkbox" checked={acceptedById[item.id] === true} onChange={(event) => setAcceptedById((current) => ({ ...current, [item.id]: event.target.checked }))} /> Подтверждаю условия и сумму</label>
                  <div>
                    <button type="button" className="u2-btn is-secondary is-sm" onClick={() => setPreviewById((current) => ({ ...current, [item.id]: undefined }))}>Закрыть</button>
                    <button type="button" className="u2-btn is-primary is-sm" disabled={busy || acceptedById[item.id] !== true} onClick={() => void run(item.id, async () => {
                      const operationKey = renewOperationKeyById[item.id];
                      if (!operationKey) throw new Error('Не удалось создать ключ операции продления');
                      await renewLongRental(item.id, preview, operationKey);
                      setPreviewById((current) => ({ ...current, [item.id]: undefined }));
                    })}>Оплатить продление</button>
                  </div>
                </div>
              ) : null}

              {refundConfirmId === item.id ? (
                <div className="sms-longNumber__renewal sms-longNumber__refundConfirm">
                  <div><strong>Возврат за номер</strong><span>{formatUsd(item.finalAmount)}</span></div>
                  <p>{item.refund.rule}</p>
                  <p>Если на номер поступило хотя бы одно SMS, возврат недоступен. Номер прекратит действовать после подтверждения поставщиком; только затем оплата будет зачислена на баланс.</p>
                  <div>
                    <button type="button" className="u2-btn is-secondary is-sm" disabled={busy} onClick={() => setRefundConfirmId(null)}>Не возвращать</button>
                    <button type="button" className="u2-btn is-danger is-sm" disabled={busy} onClick={() => void run(item.id, async () => {
                      await refundLongRental(item.id);
                      setRefundConfirmId(null);
                    })}>Подтвердить возврат</button>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
        {!loading && items.length > 0 && filteredItems.length === 0 ? (
          <div className="sms-longNumbers__noResults">
            <SmsIcon name="search" />
            <span><strong>Ничего не найдено</strong><small>Измените тип, сервис или поисковый запрос.</small></span>
            <button type="button" className="u2-btn is-secondary is-sm" onClick={() => { setCapabilityFilter('all'); setServiceFilter('all'); setSearchQuery(''); }}>Сбросить фильтры</button>
          </div>
        ) : null}
      </div>
      {sharedItems.length > 0 ? <section className="sms-longNumbers__shared" aria-label="Номера, доступные по ссылке">
        <h3>Доступ по ссылке</h3>
        <p>Эти номера вам не принадлежат. Вы можете открыть сообщения по сохранённой ссылке; владелец может отозвать доступ.</p>
        <div className="sms-longNumbers__sharedList">
          {sharedItems.map((item) => <article key={item.id}>
            <div><strong>{presentPhoneNumber(item.phoneNumber).displayFullNumber}</strong><small>{stateLabel(item.state)}{item.endsAt ? ` · до ${formatDateTime(item.endsAt)}` : ''}</small></div>
            <a className="u2-btn is-secondary is-sm" href={item.publicInboxPath}>Открыть SMS</a>
            <button type="button" className="u2-btn is-secondary is-sm" disabled={pendingId === item.id} onClick={() => void run(item.id, () => removeSharedLongRentalNumber(item.id))}>Убрать</button>
          </article>)}
        </div>
      </section> : null}
    </section>
  );
};

export default LongRentalNumbersSection;
